import {
  MaintenanceReason,
  MaintenanceSeverity,
  MaintenanceCategory,
  MaintenanceStatus,
} from '@/types/common';

// Maintenance Reason Mappings
export const getMaintenanceReasonLabel = (
  reason: MaintenanceReason
): string => {
  const labels: Record<MaintenanceReason, string> = {
    [MaintenanceReason.HUB_AUDIT]: 'Hub Audit',
    [MaintenanceReason.SCHEDULED_MAINTENANCE]: 'Scheduled Maintenance',
    [MaintenanceReason.LINKED_TO_INCIDENT]: 'Linked to Incident',
    [MaintenanceReason.MITRA_REPORT]: 'Mitra Report',
    [MaintenanceReason.POST_HANDOVER_INSPECTION]: 'Post Handover Inspection',
  };
  return labels[reason];
};

export const getMaintenanceReasonOptions = () => [
  {
    label: getMaintenanceReasonLabel(MaintenanceReason.HUB_AUDIT),
    value: MaintenanceReason.HUB_AUDIT,
  },
  {
    label: getMaintenanceReasonLabel(MaintenanceReason.SCHEDULED_MAINTENANCE),
    value: MaintenanceReason.SCHEDULED_MAINTENANCE,
  },
  {
    label: getMaintenanceReasonLabel(MaintenanceReason.LINKED_TO_INCIDENT),
    value: MaintenanceReason.LINKED_TO_INCIDENT,
  },
  {
    label: getMaintenanceReasonLabel(MaintenanceReason.MITRA_REPORT),
    value: MaintenanceReason.MITRA_REPORT,
  },
  {
    label: getMaintenanceReasonLabel(
      MaintenanceReason.POST_HANDOVER_INSPECTION
    ),
    value: MaintenanceReason.POST_HANDOVER_INSPECTION,
  },
];

// Maintenance Severity Mappings
export const getMaintenanceSeverityLabel = (
  severity: MaintenanceSeverity
): string => {
  const labels: Record<MaintenanceSeverity, string> = {
    [MaintenanceSeverity.LOW]: 'Low',
    [MaintenanceSeverity.MEDIUM]: 'Medium',
    [MaintenanceSeverity.HIGH]: 'High',
    [MaintenanceSeverity.CRITICAL]: 'Critical',
  };
  return labels[severity];
};

export const getMaintenanceSeverityOptions = () => [
  {
    label: getMaintenanceSeverityLabel(MaintenanceSeverity.LOW),
    value: MaintenanceSeverity.LOW,
  },
  {
    label: getMaintenanceSeverityLabel(MaintenanceSeverity.MEDIUM),
    value: MaintenanceSeverity.MEDIUM,
  },
  {
    label: getMaintenanceSeverityLabel(MaintenanceSeverity.HIGH),
    value: MaintenanceSeverity.HIGH,
  },
  {
    label: getMaintenanceSeverityLabel(MaintenanceSeverity.CRITICAL),
    value: MaintenanceSeverity.CRITICAL,
  },
];

export const getMaintenanceSeverityColor = (
  severity: MaintenanceSeverity | string
): string => {
  const severityKey =
    typeof severity === 'string' ? (severity as MaintenanceSeverity) : severity;
  const colors: Record<MaintenanceSeverity, string> = {
    [MaintenanceSeverity.LOW]: 'bg-green-100 text-green-800',
    [MaintenanceSeverity.MEDIUM]: 'bg-yellow-100 text-yellow-800',
    [MaintenanceSeverity.HIGH]: 'bg-orange-100 text-orange-800',
    [MaintenanceSeverity.CRITICAL]: 'bg-red-100 text-red-800',
  };
  return colors[severityKey] || 'bg-gray-100 text-gray-800';
};

// Maintenance Category Mappings
export const getMaintenanceCategoryLabel = (
  category: MaintenanceCategory
): string => {
  const labels: Record<MaintenanceCategory, string> = {
    [MaintenanceCategory.BATTERY_REPLACEMENT]: 'Battery Replacement',
    [MaintenanceCategory.MOTOR_BLDC_REPAIR]: 'Motor BLDC Repair',
    [MaintenanceCategory.TIRE_CHANGE]: 'Tire Change',
    [MaintenanceCategory.BRAKE_SYSTEM]: 'Brake System',
    [MaintenanceCategory.BODY_REPAIR]: 'Body Repair',
    [MaintenanceCategory.ELECTRICAL_SYSTEM]: 'Electrical System',
    [MaintenanceCategory.FRAME_DAMAGE]: 'Frame Damage',
    [MaintenanceCategory.GENERAL_MAINTENANCE]: 'General Maintenance',
    [MaintenanceCategory.OTHER]: 'Other',
  };
  return labels[category];
};

export const getMaintenanceCategoryOptions = () => [
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.BATTERY_REPLACEMENT),
    value: MaintenanceCategory.BATTERY_REPLACEMENT,
  },
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.MOTOR_BLDC_REPAIR),
    value: MaintenanceCategory.MOTOR_BLDC_REPAIR,
  },
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.TIRE_CHANGE),
    value: MaintenanceCategory.TIRE_CHANGE,
  },
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.BRAKE_SYSTEM),
    value: MaintenanceCategory.BRAKE_SYSTEM,
  },
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.BODY_REPAIR),
    value: MaintenanceCategory.BODY_REPAIR,
  },
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.ELECTRICAL_SYSTEM),
    value: MaintenanceCategory.ELECTRICAL_SYSTEM,
  },
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.FRAME_DAMAGE),
    value: MaintenanceCategory.FRAME_DAMAGE,
  },
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.GENERAL_MAINTENANCE),
    value: MaintenanceCategory.GENERAL_MAINTENANCE,
  },
  {
    label: getMaintenanceCategoryLabel(MaintenanceCategory.OTHER),
    value: MaintenanceCategory.OTHER,
  },
];

// Maintenance Status Mappings
export const getMaintenanceStatusLabel = (
  status: MaintenanceStatus
): string => {
  const labels: Record<MaintenanceStatus, string> = {
    [MaintenanceStatus.OPEN]: 'Open',
    [MaintenanceStatus.IN_PROGRESS]: 'In Progress',
    [MaintenanceStatus.COMPLETED]: 'Completed',
    [MaintenanceStatus.PENDING_APPROVAL_COMPLETION]: 'Pending Approval',
    [MaintenanceStatus.PENDING_APPROVAL_TEMP_SWAP]: 'Pending Approval',
    [MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP]: 'Pending Approval',
    [MaintenanceStatus.CLOSED]: 'Closed',
    [MaintenanceStatus.TEMP_SWAP]: 'Temporary Swap',
    [MaintenanceStatus.PENDING_APPROVAL]: 'Pending Approval',
    [MaintenanceStatus.PERM_SWAP]: 'Permanent Swap',
  };
  return labels[status];
};

export const getMaintenanceStatusOptions = () => [
  {
    label: getMaintenanceStatusLabel(MaintenanceStatus.OPEN),
    value: MaintenanceStatus.OPEN,
  },
  {
    label: getMaintenanceStatusLabel(MaintenanceStatus.IN_PROGRESS),
    value: MaintenanceStatus.IN_PROGRESS,
  },

  {
    label: getMaintenanceStatusLabel(MaintenanceStatus.TEMP_SWAP),
    value: MaintenanceStatus.TEMP_SWAP,
  },
  {
    label: getMaintenanceStatusLabel(MaintenanceStatus.PERM_SWAP),
    value: MaintenanceStatus.PERM_SWAP,
  },
  {
    label: getMaintenanceStatusLabel(MaintenanceStatus.PENDING_APPROVAL),
    value: `${MaintenanceStatus.PENDING_APPROVAL_COMPLETION},${
      MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP
    },${MaintenanceStatus.PENDING_APPROVAL_TEMP_SWAP}`,
  },
  {
    label: getMaintenanceStatusLabel(MaintenanceStatus.COMPLETED),
    value: MaintenanceStatus.COMPLETED,
  },
  {
    label: getMaintenanceStatusLabel(MaintenanceStatus.CLOSED),
    value: MaintenanceStatus.CLOSED,
  },
];

// Cost Responsibility Mappings
export const MAINTENANCE_COST_RESPONSIBILITIES = [
  'DASH_ELECTRIC',
  'CLIENT',
  'MITRA',
  'OEM',
] as const;

export const getCostResponsibilityLabel = (responsibility: string): string => {
  const labels: Record<string, string> = {
    DASH_ELECTRIC: 'Dash Electric',
    CLIENT: 'Client',
    MITRA: 'Mitra',
    OEM: 'OEM',
  };
  return labels[responsibility] || responsibility;
};

export const getCostResponsibilityOptions = () => [
  {
    label: getCostResponsibilityLabel('DASH_ELECTRIC'),
    value: 'DASH_ELECTRIC',
  },
  { label: getCostResponsibilityLabel('CLIENT'), value: 'CLIENT' },
  { label: getCostResponsibilityLabel('MITRA'), value: 'MITRA' },
  { label: getCostResponsibilityLabel('OEM'), value: 'OEM' },
];
